import { RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createResizeObserver } from '../../utils/createResizeObserver';
import { fitTabBar, TabBarFit, TabBarFitInput } from './fitActionCount';

export const PILL_MEASURE_ATTR = 'data-pill';
export const ACTION_MEASURE_ATTR = 'data-action';

interface FitSubject {
    pillIds: string[];
    pillLabels: string[];
    activePillId: string | undefined;
    actionIds: string[];
    hasLeading: boolean;
    leadingCollapsible: boolean;
    leadingShrinkable: boolean;
    foldPills: boolean;
    pillGap: number;
    gap: number;
}

interface FitProbes {
    triggerRef: RefObject<HTMLElement>;
    leadingNaturalRef: RefObject<HTMLElement>;
    leadingFloorRef: RefObject<HTMLElement>;
}

const sameFit = (a: TabBarFit | null, b: TabBarFit | null) =>
    a === b ||
    (!!a &&
        !!b &&
        a.foldedPills.join() === b.foldedPills.join() &&
        a.leadingCollapsed === b.leadingCollapsed &&
        a.visibleActions === b.visibleActions &&
        a.showTrigger === b.showTrigger);

interface InlineWidths {
    pillWidths: number[];
    actionWidths: number[];
}

/** Natural widths of every inline pill and action in the row, or null if any of them is not rendered inline. */
function measureInlineWidths(row: HTMLElement, pillIds: string[], actionIds: string[]): InlineWidths | null {
    const elementOf = (attr: string, id: string) => row.querySelector<HTMLElement>(`[${attr}="${id}"]`);
    const pillElements = pillIds.map((id) => elementOf(PILL_MEASURE_ATTR, id));
    const actionElements = actionIds.map((id) => elementOf(ACTION_MEASURE_ATTR, id));
    if ([...pillElements, ...actionElements].includes(null)) return null;
    return {
        pillWidths: pillElements.map((element) => element?.offsetWidth ?? 0),
        actionWidths: actionElements.map((element) => element?.offsetWidth ?? 0),
    };
}

/** The leading's natural and narrowest widths from its hidden probes, or null when there is no leading. */
function readLeadingFit(probes: FitProbes, subject: FitSubject): TabBarFitInput['leading'] {
    const naturalProbe = probes.leadingNaturalRef.current;
    if (!naturalProbe) return null;
    const natural = naturalProbe.offsetWidth;
    const floorProbe = probes.leadingFloorRef.current;
    return {
        natural,
        floor: subject.leadingShrinkable && floorProbe ? floorProbe.scrollWidth : natural,
        collapsible: subject.leadingCollapsible,
    };
}

/** Assembles the fit input from the row's width, the cached item widths and the hidden probes. */
function toFitInput(available: number, subject: FitSubject, probes: FitProbes, widths: InlineWidths): TabBarFitInput {
    return {
        available,
        pillWidths: widths.pillWidths,
        activePillIndex: subject.activePillId === undefined ? -1 : subject.pillIds.indexOf(subject.activePillId),
        leading: readLeadingFit(probes, subject),
        actionWidths: widths.actionWidths,
        triggerWidth: probes.triggerRef.current?.offsetWidth ?? 0,
        pillGap: subject.pillGap,
        gap: subject.gap,
        foldPills: subject.foldPills,
    };
}

/**
 * Which pills fold, whether the leading folds and how many actions stay inline in the bar row. Every pill and action is
 * rendered once (fit = null) so its natural width can be cached; the fit then reads only the row's width, that cache and
 * the hidden probes, so nothing it hides ever feeds back into its inputs.
 */
export function useFittedActionCount(rowRef: RefObject<HTMLElement>, subject: FitSubject, probes: FitProbes): TabBarFit {
    const { pillIds, pillLabels, activePillId, actionIds, hasLeading, leadingCollapsible, leadingShrinkable, foldPills } = subject;
    const measureKey = `${pillIds.map((id, index) => `${id}:${pillLabels[index]}`).join('|')}#${activePillId ?? ''}#${actionIds.join('|')}`;
    const [fit, setFit] = useState<TabBarFit | null>(null);
    const fitRef = useRef<TabBarFit | null>(null);
    const measuredKeyRef = useRef('');
    const pillWidthsRef = useRef<number[]>([]);
    const actionWidthsRef = useRef<number[]>([]);
    const latestRef = useRef({ subject, probes, measureKey });
    latestRef.current = { subject, probes, measureKey };

    const commit = (next: TabBarFit | null) => {
        if (sameFit(fitRef.current, next)) return;
        fitRef.current = next;
        setFit(next);
    };

    /**
     * Re-fits the row. Keeps the current fit while the row is missing or not laid out (clientWidth 0). When the measure
     * key changed, widths are re-measured only from the all-inline render (fit = null), then the fit is computed and committed.
     */
    const refit = () => {
        const row = rowRef.current;
        if (!row) return;
        const { subject: current, probes: currentProbes, measureKey: currentKey } = latestRef.current;
        const available = row.clientWidth;
        if (available === 0) return; // not laid out (hidden tab); the row observer refits once it is
        if (measuredKeyRef.current !== currentKey) {
            // A folded render can still be on screen after commit(null), so measure only once every item is inline.
            if (fitRef.current !== null) {
                commit(null); // re-render with everything inline, then measure
                return;
            }
            const widths = measureInlineWidths(row, current.pillIds, current.actionIds);
            if (!widths) {
                commit(null);
                return;
            }
            pillWidthsRef.current = widths.pillWidths;
            actionWidthsRef.current = widths.actionWidths;
            measuredKeyRef.current = currentKey;
        }
        commit(
            fitTabBar(
                toFitInput(available, current, currentProbes, {
                    pillWidths: pillWidthsRef.current,
                    actionWidths: actionWidthsRef.current,
                }),
            ),
        );
    };

    useLayoutEffect(refit, [measureKey, fit, leadingCollapsible, leadingShrinkable, foldPills]); // eslint-disable-line react-hooks/exhaustive-deps -- refit reads the latest props from latestRef

    useEffect(() => {
        const observer = createResizeObserver(() => refit());
        [rowRef.current, probes.leadingNaturalRef.current].forEach((element) => element && observer?.observe(element));
        return () => observer?.disconnect();
    }, [hasLeading]); // eslint-disable-line react-hooks/exhaustive-deps -- the refs are stable; the leading probe mounts with the leading

    useEffect(() => {
        if (typeof document === 'undefined' || !document.fonts?.ready) return;
        let cancelled = false;
        document.fonts.ready.then(() => {
            if (cancelled) return;
            measuredKeyRef.current = '';
            refit();
        });
        return () => {
            cancelled = true;
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- refit reads the latest props from latestRef

    const isMeasured = measuredKeyRef.current === measureKey;
    return fit && isMeasured ? fit : { foldedPills: [], leadingCollapsed: false, visibleActions: actionIds.length, showTrigger: false };
}
